const rawEnvApi = (import.meta.env.VITE_API_URL as string | undefined)?.trim();

const stripTrailingSlashes = (value: string) => value.replace(/\/+$/, '');

const deriveBaseFromWindow = () => {
  const { protocol, hostname, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:4000`;
  }
  if (hostname.endsWith('.inktap.ca')) {
    return 'https://api.inktap.ca';
  }
  if (port) {
    return `${protocol}//${hostname}:${port}`;
  }
  return `${protocol}//${hostname}`;
};

const SESSION_STORAGE_KEY = 'inktap:session:v3';

const resolveApiBaseUrl = () => {
  const sanitizedEnv = rawEnvApi && rawEnvApi.length > 0 ? stripTrailingSlashes(rawEnvApi) : undefined;

  if (typeof window === 'undefined') {
    return sanitizedEnv ?? 'http://localhost:4000';
  }

  const fallback = stripTrailingSlashes(deriveBaseFromWindow());

  if (!sanitizedEnv) {
    return fallback;
  }

  const isHttps = window.location.protocol === 'https:';
  const isLocalEnv = /^https?:\/\/(localhost|127\.0\.0\.1)(?::\d+)?$/i.test(sanitizedEnv);

  if (isHttps && isLocalEnv) {
    return fallback;
  }

  return sanitizedEnv;
};

const API_BASE_URL = stripTrailingSlashes(resolveApiBaseUrl());

const clearExpiredSession = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw) as { expiresAt?: string };
    if (parsed.expiresAt && Date.parse(parsed.expiresAt) <= Date.now()) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Failed to read stored session metadata', error);
  }
};

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }
  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
  }
  return { ...(headers as Record<string, string>) };
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...normalizeHeaders(init?.headers)
  };

  clearExpiredSession();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: init?.credentials ?? 'include'
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    if (response.status === 401 && typeof window !== 'undefined') {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    }
    throw { status: response.status, data };
  }

  return data as T;
}

export { API_BASE_URL, request };
